// Procedurally generated topographic contour lines for the welcome background.
// buildTopoContours(seed) is pure: the seed only shifts the phase of the sine
// wobbles, so every seed yields a different landform with the same overall
// character. A single centred "summit" emits a stack of nested rings that
// radiate out to cover the screen; every 5th ring is an index contour, drawn
// bolder, the way real maps mark them. Colour (blue core -> teal edges) comes
// from the radial stroke gradient defined in WelcomeScreen.

type Contour = { id: string; pts: [number, number][]; d: string; w: number; o: number };

const STEPS = 72;
const TAU = Math.PI * 2;

// Catmull-Rom -> cubic-bezier smoothing for a closed loop of points.
function smoothClosed(pts: [number, number][]): string {
  const n = pts.length;
  let d = `M${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}`;
  for (let i = 0; i < n; i++) {
    const [p0x, p0y] = pts[(i - 1 + n) % n];
    const [p1x, p1y] = pts[i];
    const [p2x, p2y] = pts[(i + 1) % n];
    const [p3x, p3y] = pts[(i + 2) % n];
    const c1x = p1x + (p2x - p0x) / 6;
    const c1y = p1y + (p2y - p0y) / 6;
    const c2x = p2x - (p3x - p1x) / 6;
    const c2y = p2y - (p3y - p1y) / 6;
    d += `C${c1x.toFixed(1)} ${c1y.toFixed(1)} ${c2x.toFixed(1)} ${c2y.toFixed(1)} ${p2x.toFixed(1)} ${p2y.toFixed(1)}`;
  }
  return `${d}Z`;
}

// One contour ring: a circle of radius r warped by layered sines so it reads as
// an organic elevation line, flattened vertically by `squash`.
function ring(cx: number, cy: number, r: number, seed: number, squash: number): [number, number][] {
  const pts: [number, number][] = [];
  for (let i = 0; i < STEPS; i++) {
    const a = (i / STEPS) * TAU;
    const wobble =
      Math.sin(a * 2 + seed) * 0.55 +
      Math.sin(a * 3 + seed * 1.7) * 0.28 +
      Math.sin(a * 5 + seed * 0.6) * 0.17;
    const rr = r * (1 + 0.25 * wobble);
    pts.push([cx + Math.cos(a) * rr, cy + Math.sin(a) * rr * squash]);
  }
  return pts;
}

// Cursor warp: Gaussian falloff (in viewBox units) around the pointer.
// WARP_SIGMA sets the area of effect; the cutoff (~3 sigma, where the push
// falls below the perceptible threshold) just bounds the per-point work.
// WARP_EPS softens the push direction near the pointer so the displacement
// passes smoothly through zero as the cursor crosses a line, instead of
// flipping sides with a jump.
const WARP_SIGMA = 120;
const WARP_SIGMA2 = 2 * WARP_SIGMA * WARP_SIGMA;
const WARP_CUTOFF2 = 3 * WARP_SIGMA * (3 * WARP_SIGMA);
const WARP_EPS2 = 60 * 60;

// Push a ring's points radially away from (px, py), scaled by `amp` (peak
// displacement in viewBox units), and return the re-smoothed path. Returns
// null when no point moves perceptibly so callers can skip the DOM write.
export function warpContour(
  pts: [number, number][],
  px: number,
  py: number,
  amp: number,
): string | null {
  let moved = false;
  const out: [number, number][] = new Array(pts.length);
  for (let i = 0; i < pts.length; i++) {
    const [x, y] = pts[i];
    const dx = x - px;
    const dy = y - py;
    const d2 = dx * dx + dy * dy;
    if (d2 > WARP_CUTOFF2) {
      out[i] = pts[i];
      continue;
    }
    const push = Math.exp(-d2 / WARP_SIGMA2) * amp;
    if (Math.abs(push) < 0.05) {
      out[i] = pts[i];
      continue;
    }
    moved = true;
    const soft = Math.sqrt(d2 + WARP_EPS2);
    out[i] = [x + (dx / soft) * push, y + (dy / soft) * push];
  }
  return moved ? smoothClosed(out) : null;
}

export const TOPO_VIEWBOX = { w: 1200, h: 760 };

// Single peak at the centre of the canvas. ~30 rings reach past the corners so
// the lines bleed off every edge; the inner rings are cleared by the radial mask
// so the hero content sits in a clean pocket. Slightly squashed (wider than tall)
// so it reads as a landform on a wide screen rather than a bullseye.
const CENTER_X = TOPO_VIEWBOX.w / 2;
const CENTER_Y = TOPO_VIEWBOX.h / 2;
const RING_COUNT = 30;
const BASE_R = 110;
const STEP = 24;
const SQUASH = 0.74;

export function buildTopoContours(seed: number): Contour[] {
  const out: Contour[] = [];
  for (let i = 0; i < RING_COUNT; i++) {
    const isIndex = i % 5 === 0;
    const pts = ring(CENTER_X, CENTER_Y, BASE_R + i * STEP, seed + i * 0.08, SQUASH);
    out.push({
      id: String(i),
      pts,
      d: smoothClosed(pts),
      w: isIndex ? 1 : 0.6,
      o: isIndex ? 0.6 : 0.38,
    });
  }
  return out;
}
